/**
 * 🎬 Highlight Reel Modal — Feb 2026
 *
 * Pops on-demand from the Victory terminal banner. Shows:
 *   1. A dramatic FINISHING BLOW replay of the winning attack
 *      (re-uses the BattlePage effect aesthetic — lunge + flash +
 *      floating damage number).
 *   2. A polished Victory Card with the hero, the winning skill,
 *      total damage dealt over the fight, turns survived, and
 *      OMNIMUSCLE branding.
 *   3. Three share actions:
 *        📥 Download as PNG (via html-to-image)
 *        🐦 Share to X (prefilled tweet)
 *        📋 Copy battle summary to clipboard
 *
 * Pure presentational — receives the finished `battle` object from
 * the parent and reads `battle.log`, `battle.player`, `battle.enemy`.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import * as htmlToImage from "html-to-image";

const REPLAY_HOLD_MS = 1400;   // how long the FINISHING BLOW frame lingers
const REPLAY_ATTACK_MS = 800;  // attacker lunge + target shake duration


export default function HighlightReelModal({ battle, onClose }) {
  const [phase, setPhase] = useState("intro");  // intro → strike → card
  const cardRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  // Identify the winning blow = last player atk/leech/ultimate entry that
  // dealt damage (skip system lines and heal/absorb).
  const winningEntry = findWinningEntry(battle?.log || []);
  const player = battle?.player || {};
  const enemy = battle?.enemy || {};

  // Stats summary
  const totalDamage = (battle?.log || [])
    .filter((e) => e.actor === "player" && e.damage > 0)
    .reduce((acc, e) => acc + e.damage, 0);
  const totalHealed = (battle?.log || [])
    .filter((e) => e.actor === "player" && e.healed > 0)
    .reduce((acc, e) => acc + e.healed, 0);
  const turnsSurvived = battle?.turn_number || (battle?.log || []).length || 1;
  const playerEntries = (battle?.log || []).filter((e) => e.actor === "player");
  const critCount = playerEntries.filter((e) => e.is_crit).length;

  // Phase scheduler: intro (350ms) → strike (REPLAY_ATTACK_MS) → card
  useEffect(() => {
    const t1 = setTimeout(() => setPhase("strike"), 350);
    const t2 = setTimeout(() => setPhase("card"), 350 + REPLAY_HOLD_MS);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  // Escape key closes
  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const onDownload = async () => {
    if (!cardRef.current) return;
    setExporting(true);
    try {
      const dataUrl = await htmlToImage.toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#0a0008",
      });
      const link = document.createElement("a");
      link.download = `omnimuscle-victory-${Date.now()}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("📥 Victory Card saved!");
    } catch (e) {
      console.error(e);
      toast.error("Couldn't render the card. Try again?");
    } finally { setExporting(false); }
  };

  const onShareX = () => {
    const skill = winningEntry?.skill_name || "a finishing blow";
    const enemyName = enemy?.name || "the foe";
    const text = encodeURIComponent(
      `Just took down ${enemyName} with ${skill} in OMNIMUSCLE ⚔️ ` +
      `${totalDamage} total damage · ${turnsSurvived} turns · ` +
      `#OmniMuscle #PhoenixRising`
    );
    const url = encodeURIComponent(window.location.origin + "/battle");
    window.open(
      `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      "_blank",
      "noopener,noreferrer,width=600,height=520"
    );
  };

  const onCopySummary = async () => {
    const lines = [
      `⚔️ OMNIMUSCLE Victory — ${player.name} vs ${enemy.name}`,
      `Turns survived: ${turnsSurvived} · Total damage: ${totalDamage}`,
      ...(critCount > 0 ? [`💥 ${critCount} critical hit${critCount > 1 ? "s" : ""}`] : []),
      ...(totalHealed > 0 ? [`💚 ${totalHealed} HP healed`] : []),
      ``,
      `Finishing blow: ${winningEntry?.skill_name || "—"} ${winningEntry?.skill_icon || ""} ` +
        `(${winningEntry?.damage || 0} dmg${winningEntry?.is_crit ? " · CRIT" : ""}${winningEntry?.type_label === "true" ? " · TRUE" : ""})`,
      ``,
      `Forged at omnimuscle.com ⚔️🔥`,
    ].join("\n");
    try {
      await navigator.clipboard.writeText(lines);
      toast.success("📋 Battle summary copied.");
    } catch {
      toast.error("Clipboard blocked — long-press to copy.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 backdrop-blur-sm reel-fade-in"
      onClick={onClose}
      data-testid="highlight-reel-modal"
    >
      <ReelStyles />
      <div
        className="relative w-full max-w-2xl mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute -top-3 -right-3 w-9 h-9 rounded-full bg-amber-500 text-black font-black text-lg z-10 hover:bg-amber-400 shadow-lg"
          data-testid="reel-close-btn"
          aria-label="Close highlight reel"
        >
          ✕
        </button>

        {/* Replay stage (phases intro + strike) */}
        {phase !== "card" && (
          <div className="rounded-2xl border-2 border-amber-500/60 bg-gradient-to-br from-amber-950/40 via-black to-red-950/40 p-8 text-center" data-testid={`reel-phase-${phase}`}>
            <div className={`text-[11px] uppercase tracking-[0.4em] text-amber-300/80 font-black ${phase === "intro" ? "intro-pop" : ""}`}>
              Highlight Reel
            </div>
            <div className={`text-4xl sm:text-5xl font-black text-amber-300 my-3 ${phase === "intro" ? "intro-pop" : ""}`}
                 style={{ fontFamily: "Anton, sans-serif" }}>
              FINISHING BLOW
            </div>

            <div className="relative h-44 sm:h-52 flex items-center justify-center">
              {/* Player → strikes → Enemy */}
              <CombatantPortrait
                actor={player}
                role={phase === "strike" ? "attacker" : "idle"}
                side="player"
              />
              <div className="mx-4 text-3xl font-black text-amber-400">⚔️</div>
              <CombatantPortrait
                actor={enemy}
                role={phase === "strike" ? "target" : "idle"}
                side="enemy"
                killed={phase === "strike"}
                hitDamage={winningEntry?.damage}
                isCrit={winningEntry?.is_crit}
                isTrue={winningEntry?.type_label === "true"}
              />
            </div>

            <div className="mt-4 text-amber-200 text-sm">
              {winningEntry ? (
                <>
                  <span className="text-2xl mr-1">{winningEntry.skill_icon || "✨"}</span>
                  <span className="font-black">{winningEntry.skill_name}</span>
                  <span className="ml-2 text-amber-400 font-mono">
                    {winningEntry.damage} dmg
                  </span>
                  {winningEntry.is_crit && (
                    <span className="ml-2 text-amber-300 font-black">💥 CRIT</span>
                  )}
                  {winningEntry.type_label === "true" && (
                    <span className="ml-2 text-fuchsia-300 font-black">TRUE</span>
                  )}
                </>
              ) : (
                <span className="italic text-amber-100/70">— end of fight —</span>
              )}
            </div>
          </div>
        )}

        {/* Victory Card (phase = card) */}
        {phase === "card" && (
          <>
            {/* The actual PNG-exported card lives inside this ref */}
            <div
              ref={cardRef}
              className="rounded-2xl overflow-hidden border-2 border-amber-500/70 reel-card-in"
              data-testid="victory-card"
              style={{
                background: "radial-gradient(ellipse at top, #1f0a18 0%, #050003 70%, #000 100%)",
              }}
            >
              {/* Top brand bar */}
              <div className="px-5 py-3 flex items-center justify-between border-b border-amber-500/30 bg-black/40">
                <div className="text-[11px] tracking-[0.4em] uppercase font-black text-amber-300">
                  OMNIMUSCLE · VICTORY
                </div>
                <div className="text-[10px] text-amber-200/60 font-mono">
                  {new Date().toISOString().slice(0, 10)}
                </div>
              </div>

              {/* Hero spotlight */}
              <div className="px-6 py-6 text-center">
                <div className="text-7xl mb-2 victory-glow">{player.icon || "🦅"}</div>
                <div
                  className="text-3xl sm:text-4xl font-black text-amber-200"
                  style={{ fontFamily: "Anton, sans-serif" }}
                >
                  {player.name || "Hero"}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-amber-400/70 mt-0.5">
                  {player.class_id || "no class"} · Lv {player.level || "?"}
                </div>

                {/* Outcome line */}
                <div className="mt-4 text-base text-amber-100/90">
                  triumphed over <span className="font-black text-red-300">{enemy.name || "the foe"}</span>
                </div>

                {/* Finisher panel */}
                {winningEntry && (
                  <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-amber-500/40 bg-amber-900/20">
                    <span className="text-2xl">{winningEntry.skill_icon || "✨"}</span>
                    <span className="text-sm font-black text-amber-100">{winningEntry.skill_name}</span>
                    <span className="text-xs font-mono text-amber-300">{winningEntry.damage} dmg</span>
                    {winningEntry.is_crit && <span className="text-xs font-black text-amber-300">CRIT</span>}
                    {winningEntry.type_label === "true" && <span className="text-xs font-black text-fuchsia-300">TRUE</span>}
                  </div>
                )}
              </div>

              {/* Stat strip */}
              <div className="grid grid-cols-3 border-t border-amber-500/30 bg-black/40">
                <StatCell label="Damage Dealt" value={totalDamage.toLocaleString()} icon="⚔️" />
                <StatCell label="Turns Survived" value={turnsSurvived} icon="⏳" />
                <StatCell label="Crits Landed" value={critCount} icon="💥" />
              </div>

              {/* Footer */}
              <div className="px-5 py-2 text-center border-t border-amber-500/30 bg-black/60">
                <div className="text-[10px] tracking-widest uppercase text-amber-400/70 font-black">
                  ⚔️ forged at omnimuscle.com 🔥
                </div>
              </div>
            </div>

            {/* Share controls (NOT in the PNG export) */}
            <div className="mt-4 flex flex-wrap gap-2 justify-center" data-testid="reel-share-bar">
              <button
                onClick={onDownload}
                disabled={exporting}
                data-testid="reel-download-btn"
                className="px-5 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-black font-black text-sm shadow-lg"
              >
                {exporting ? "Rendering…" : "📥 Download PNG"}
              </button>
              <button
                onClick={onShareX}
                data-testid="reel-share-x-btn"
                className="px-5 py-2 rounded-lg bg-sky-500 hover:bg-sky-400 text-black font-black text-sm shadow-lg"
              >
                𝕏 Share on X
              </button>
              <button
                onClick={onCopySummary}
                data-testid="reel-copy-summary-btn"
                className="px-5 py-2 rounded-lg border border-amber-500/60 hover:bg-amber-900/30 text-amber-200 font-black text-sm"
              >
                📋 Copy Summary
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


/* ──────────────────────────────────────────────────────────────
 * Helpers
 * ────────────────────────────────────────────────────────────── */
function findWinningEntry(log) {
  // Walk backwards for the last damage-dealing player move.
  for (let i = log.length - 1; i >= 0; i--) {
    const e = log[i];
    if (e?.actor !== "player") continue;
    if (!["atk_p", "atk_m", "atk_true", "ultimate", "leech"].includes(e.kind)) continue;
    if ((e.damage || 0) > 0) return e;
  }
  return null;
}


function CombatantPortrait({ actor, role, side, killed, hitDamage, isCrit, isTrue }) {
  const isEnemy = side === "enemy";
  const tint = isEnemy
    ? "border-red-700/60 bg-gradient-to-br from-red-950/50 to-black"
    : "border-amber-700/60 bg-gradient-to-br from-amber-950/40 to-black";
  const animClass =
    role === "attacker" ? (isEnemy ? "reel-lunge-down" : "reel-lunge-up")
    : role === "target" ? (isTrue ? "reel-hit-true" : isCrit ? "reel-hit-crit" : "reel-hit-shake")
    : "";
  return (
    <div className={`relative rounded-2xl border-2 ${tint} p-4 w-28 sm:w-32 text-center ${animClass}`}>
      {/* Killed-overlay */}
      {killed && isEnemy && (
        <div className="pointer-events-none absolute inset-0 rounded-2xl bg-red-700/30 reel-flash" />
      )}
      <div className={`text-5xl ${killed ? "grayscale opacity-40" : ""}`}>{actor.icon || (isEnemy ? "🎯" : "🦅")}</div>
      <div className="mt-1 text-[11px] font-black text-amber-200 truncate">{actor.name || "—"}</div>
      <div className="text-[9px] text-neutral-400 uppercase tracking-widest">
        Lv {actor.level || "?"}
      </div>

      {/* Floating dmg number — only on target during strike phase */}
      {role === "target" && hitDamage > 0 && (
        <div
          className={`pointer-events-none absolute -top-3 right-0 text-2xl font-black ${
            isTrue ? "text-fuchsia-300 drop-shadow-[0_0_8px_rgba(232,121,249,0.9)]"
            : isCrit ? "text-amber-300 drop-shadow-[0_0_10px_rgba(252,211,77,0.9)]"
            : "text-red-300 drop-shadow-[0_0_6px_rgba(248,113,113,0.7)]"
          } reel-dmg-float`}
        >
          -{hitDamage}
        </div>
      )}
    </div>
  );
}


function StatCell({ label, value, icon }) {
  return (
    <div className="px-4 py-3 text-center border-l border-amber-500/20 first:border-l-0">
      <div className="text-2xl">{icon}</div>
      <div className="text-xl sm:text-2xl font-black text-amber-200 mt-0.5" style={{ fontFamily: "Anton, sans-serif" }}>
        {value}
      </div>
      <div className="text-[9px] uppercase tracking-widest text-amber-400/60 mt-0.5">
        {label}
      </div>
    </div>
  );
}


/* ──────────────────────────────────────────────────────────────
 * Local keyframes (separate from BattlePage's so they don't collide)
 * ────────────────────────────────────────────────────────────── */
function ReelStyles() {
  return (
    <style>{`
      @keyframes reelFadeIn { from { opacity: 0; } to { opacity: 1; } }
      @keyframes introPop {
        0%   { transform: scale(0.85); opacity: 0; }
        70%  { transform: scale(1.04); opacity: 1; }
        100% { transform: scale(1);   opacity: 1; }
      }
      @keyframes reelCardIn {
        0%   { transform: translateY(20px) scale(0.96); opacity: 0; }
        100% { transform: translateY(0)    scale(1);    opacity: 1; }
      }
      @keyframes reelLungeUp {
        0%   { transform: translate(0,0) scale(1); }
        50%  { transform: translate(40px,-12px) scale(1.08); }
        100% { transform: translate(0,0) scale(1); }
      }
      @keyframes reelLungeDown {
        0%   { transform: translate(0,0) scale(1); }
        50%  { transform: translate(-40px,12px) scale(1.08); }
        100% { transform: translate(0,0) scale(1); }
      }
      @keyframes reelHitShake {
        0%, 100% { transform: translateX(0); }
        20%  { transform: translateX(-8px) rotate(-1deg); }
        40%  { transform: translateX(10px) rotate(0.8deg); }
        60%  { transform: translateX(-6px); }
        80%  { transform: translateX(4px); }
      }
      @keyframes reelHitCrit {
        0%, 100% { transform: translateX(0) scale(1); }
        20%  { transform: translateX(-12px) scale(1.05); }
        45%  { transform: translateX(14px)  scale(1.06); }
        70%  { transform: translateX(-8px)  scale(1.02); }
      }
      @keyframes reelHitTrue {
        0%, 100% { transform: scale(1); filter: hue-rotate(0deg); }
        50% { transform: scale(1.08); filter: hue-rotate(280deg) saturate(2); }
      }
      @keyframes reelFlash {
        0%   { opacity: 0; }
        30%  { opacity: 1; }
        100% { opacity: 0; }
      }
      @keyframes reelDmgFloat {
        0%   { opacity: 0; transform: translateY(10px) scale(0.85); }
        30%  { opacity: 1; transform: translateY(-2px) scale(1.15); }
        70%  { opacity: 1; transform: translateY(-22px) scale(1); }
        100% { opacity: 0; transform: translateY(-38px) scale(0.95); }
      }
      @keyframes victoryGlow {
        0%, 100% { filter: drop-shadow(0 0 8px rgba(252,211,77,0.3)); }
        50%      { filter: drop-shadow(0 0 22px rgba(252,211,77,0.7)); }
      }
      .reel-fade-in   { animation: reelFadeIn 0.25s ease-out; }
      .intro-pop      { animation: introPop 0.45s ease-out; }
      .reel-card-in   { animation: reelCardIn 0.55s cubic-bezier(0.2,0.9,0.3,1.1); }
      .reel-lunge-up    { animation: reelLungeUp   0.8s ease-out; }
      .reel-lunge-down  { animation: reelLungeDown 0.8s ease-out; }
      .reel-hit-shake { animation: reelHitShake 0.7s ease-in-out 0.18s; }
      .reel-hit-crit  { animation: reelHitCrit  0.8s ease-in-out 0.18s; }
      .reel-hit-true  { animation: reelHitTrue  0.8s ease-in-out 0.18s; }
      .reel-flash     { animation: reelFlash    0.7s ease-out 0.18s forwards; }
      .reel-dmg-float { animation: reelDmgFloat 1.1s ease-out 0.25s forwards; }
      .victory-glow   { animation: victoryGlow  2.4s ease-in-out infinite; }
    `}</style>
  );
}
